import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, LogIn, LogOut, Menu, User, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAccountData } from '../context/AccountContext';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import SearchBar from './SearchBar';
import {
  isMobileViewport,
  findHeroBottom,
  shouldUseLiquidHeader,
} from '../utils/liquidHeader';

/**
 * Persistent nav — rendered on EVERY page (landing, /login, /register,
 * /account, /language …).
 *
 * Previously the links were passed in as a `homeLinks` prop that only the
 * landing page supplied, so they vanished the moment the visitor opened an
 * inner route. The canonical set now lives here so the header is identical
 * everywhere.
 *
 * `to` values are real route paths (optionally with a hash) so they resolve
 * from any page — an inner-route visitor clicking "Courses" is taken home and
 * scrolled to the section by <ScrollManager />, instead of a bare `#courses`
 * anchor silently doing nothing off the landing page.
 */
const NAV_LINKS = [
  { to: '/language', labelKey: 'nav.languages', fallback: 'Languages' },
  { to: '/#method', labelKey: 'nav.method', fallback: 'Method' },
  /* Curriculum 与 Prep 合并为一个入口：默认进入学科轨，备考轨由页内 Tab 切换。 */
  { to: '/curriculum?track=subjects', labelKey: 'nav.curriculum', fallback: 'Curriculum' },
  { to: '/colleges', labelKey: 'nav.colleges', fallback: 'Colleges' },
];

export default function AppHeader() {
  const { auth, token, logout } = useAccountData() || {};
  const { t } = useTranslation();
  const navigate = useNavigate();
  const signedIn = Boolean(auth && token);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  // Hamburger nav (Languages / Method / Curriculum / Colleges) — to the right
  // of the profile button. Toggled open/closed like mgv.gallery's dropdown.
  const [navOpen, setNavOpen] = useState(false);
  const navRef = useRef(null);

  // Mobile-only "liquid glass" header: floats in as a frosted pill after the
  // hero section scrolls out of view (see utils/liquidHeader.js).
  const [isLiquid, setIsLiquid] = useState(false);
  const location = useLocation();

  const displayName =
    auth?.firstName ||
    auth?.name?.split(' ')[0] ||
    (auth?.email ? auth.email.split('@')[0] : '');
  const avatarUrl = auth?.profileImageUrl || auth?.avatarUrl;
  const initial = (displayName?.[0] || '?').toUpperCase();

  // Start fetching the navbar avatar as early as possible (it is a small,
  // above-the-fold image served from a slow origin, so a preload link avoids
  // the "blank circle → pops in later" delay). A single <link> is reused and
  // retargeted whenever the signed-in user's avatar changes.
  const avatarPreloadRef = useRef(null);
  useEffect(() => {
    const cleanup = () => {
      if (avatarPreloadRef.current) {
        avatarPreloadRef.current.remove();
        avatarPreloadRef.current = null;
      }
    };
    if (!avatarUrl) {
      cleanup();
      return undefined;
    }
    // Only assign a real, parseable URL. An empty string / "null" / malformed
    // value makes the browser log "<link rel=preload> has an invalid href value".
    let resolved;
    try {
      resolved = new URL(avatarUrl, window.location.origin);
    } catch {
      resolved = null;
    }
    // Only http(s) URLs are valid preload targets; reject data:/blob:/relative
    // garbage that would make the browser log "<link rel=preload> invalid href".
    if (!resolved || (resolved.protocol !== 'http:' && resolved.protocol !== 'https:')) {
      cleanup();
      return undefined;
    }
    let link = avatarPreloadRef.current;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
      avatarPreloadRef.current = link;
    }
    link.href = resolved.href;
    return undefined;
  }, [avatarUrl]);
  useEffect(() => {
    return () => {
      if (avatarPreloadRef.current) {
        avatarPreloadRef.current.remove();
        avatarPreloadRef.current = null;
      }
    };
  }, []);

  // Close the account menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (e) => {
      const node = menuRef.current;
      if (node && !node.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  // Close the hamburger nav menu on outside click / Escape.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onPointerDown = (e) => {
      const node = navRef.current;
      if (node && !node.contains(e.target)) setNavOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [navOpen]);

  // Liquid-glass mode: re-evaluated on scroll/resize (rAF-throttled) and on
  // every route change, since each page has a different hero element.
  useEffect(() => {
    let ticking = false;
    const evaluate = () => {
      ticking = false;
      setIsLiquid(
        shouldUseLiquidHeader({
          mobile: isMobileViewport(
            typeof window !== 'undefined' ? window.matchMedia : undefined
          ),
          scrollY: window.scrollY,
          heroBottom: findHeroBottom(
            document,
            window.scrollY,
            window.innerHeight
          ),
        })
      );
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(evaluate);
      }
    };
    evaluate();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (ticking) window.cancelAnimationFrame(ticking);
    };
  }, [location.key]);

  const handleSignOut = () => {
    setMenuOpen(false);
    if (typeof logout === 'function') {
      logout();
    } else {
      try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      } catch {
        /* storage unavailable */
      }
    }
    navigate('/', { replace: true });
  };

  return (
    <header className={`lc-shell__header${isLiquid ? ' liquid' : ''}`}>
      <div className="lc-shell__brand-group">
        {isLiquid ? (
          <button
            type="button"
            className="lc-brand lc-brand--liquid"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label={t('nav.backToTop', 'Back to top')}
          >
            <img src="/logo.png" alt="" className="lc-brand__logo" width={28} height={28} aria-hidden="true" />
          </button>
        ) : (
          <Link to="/" className="lc-brand">
            <img src="/logo.png" alt="" className="lc-brand__logo" width={28} height={28} aria-hidden="true" />
            <span className="lc-brand__text">
              <strong>{t('nav.brand', 'Omniversail')}</strong>
              <small>{t('nav.brandSub', 'Language Center')}</small>
            </span>
          </Link>
        )}
      </div>

      <div className="lc-shell__nav-right">
        <ThemeToggle />
        <LanguageSwitcher />
        <SearchBar />
        <div className="lc-shell__actions">
          <div className="lc-account-menu" ref={menuRef}>
            {signedIn ? (
              <button
                type="button"
                className="lc-profile"
                ref={triggerRef}
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={t('nav.account', 'Account')}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    className="lc-profile__avatar"
                    width={28}
                    height={28}
                    decoding="async"
                    fetchPriority="high"
                  />
                ) : (
                  <span className="lc-profile__avatar lc-profile__avatar--fallback">{initial}</span>
                )}
                <span className="lc-profile__name">{displayName}</span>
              </button>
            ) : (
              <button
                type="button"
                className="lc-profile-icon"
                ref={triggerRef}
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={t('nav.account', 'Account')}
              >
                <User size={18} aria-hidden="true" />
              </button>
            )}
            {menuOpen && (
              <div className="lc-account-menu__panel" role="menu">
                {signedIn ? (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      className="lc-account-menu__item"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/account');
                      }}
                    >
                      <LayoutDashboard size={16} aria-hidden="true" />
                      {t('nav.dashboard', 'Dashboard')}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="lc-account-menu__item lc-account-menu__item--danger"
                      onClick={handleSignOut}
                    >
                      <LogOut size={16} aria-hidden="true" />
                      {t('nav.signOut', 'Sign out')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      role="menuitem"
                      className="lc-account-menu__item"
                      onClick={() => setMenuOpen(false)}
                    >
                      <LogIn size={16} aria-hidden="true" />
                      {t('nav.signIn', 'Sign in')}
                    </Link>
                    <Link
                      to="/register"
                      role="menuitem"
                      className="lc-account-menu__item lc-account-menu__item--primary"
                      onClick={() => setMenuOpen(false)}
                    >
                      <UserPlus size={16} aria-hidden="true" />
                      {t('nav.createAccount', 'Create account')}
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="lc-nav-toggle-wrap" ref={navRef}>
          <button
            type="button"
            className="lc-nav-toggle"
            onClick={() => setNavOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={navOpen}
            aria-label={t('nav.menu', 'Menu')}
          >
            <Menu size={20} aria-hidden="true" />
          </button>
          <ul className={`lc-nav-menu ${navOpen ? 'active' : ''}`} role="menu">
            {NAV_LINKS.map((l) => (
              <li key={l.labelKey}>
                <Link to={l.to} role="menuitem" onClick={() => setNavOpen(false)}>
                  {t(l.labelKey, l.fallback)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </header>
  );
}
